import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Target, TrendingUp, TrendingDown, Sparkles, Activity } from 'lucide-react'
import { useMustStudyTopics } from '../../../hooks/usePYQAnalytics'
import { usePYQAnalyticsStore } from '../../../stores/pyqAnalyticsStore'
import LoadingSection from '../shared/LoadingSection'
import ErrorSection from '../shared/ErrorSection'
import SubjectTabs from '../shared/SubjectTabs'
import { CONFIDENCE_COLORS, SUBJECT_COLORS } from '../../../constants/colors'
import type { TrendDirection, SubjectCode } from '../../../types'
import { AnalyticsLockedOverlay } from '@/features/payments'

// Helper to get subject color from various code formats
function getSubjectColor(subjectCode?: string, subjectName?: string): string {
  const code = (subjectCode || subjectName || '').toUpperCase()

  if (code.includes('PHY')) {
    return SUBJECT_COLORS.NEET_UG_PHY
  }
  if (code.includes('CHEM')) {
    return SUBJECT_COLORS.NEET_UG_CHEM
  }
  if (code.includes('BIO') || code.includes('BOTANY') || code.includes('ZOOLOGY')) {
    return SUBJECT_COLORS.NEET_UG_BIO
  }

  return '#6B7280' // Default gray
}

function TrendIcon({ trend }: { trend?: TrendDirection }) {
  switch (trend) {
    case 'INCREASING':
      return <TrendingUp className="w-4 h-4 text-green-600" />
    case 'DECREASING':
      return <TrendingDown className="w-4 h-4 text-red-600" />
    case 'NEW':
      return <Sparkles className="w-4 h-4 text-purple-600" />
    case 'IRREGULAR':
      return <Activity className="w-4 h-4 text-amber-500" />
    default:
      return null
  }
}

interface PredictionsSectionProps {
  isLocked?: boolean
}

export default function PredictionsSection({ isLocked = false }: PredictionsSectionProps) {
  const navigate = useNavigate()
  const { yearRangeStart, yearRangeEnd } = usePYQAnalyticsStore()
  const [selectedSubject, setSelectedSubject] = useState<SubjectCode>('NEET_UG_PHY')

  const handleTopicClick = (topicCode: string) => {
    navigate(`/analytics/past-exams/topic/${topicCode}`)
  }

  const { data, isLoading, isError, refetch } = useMustStudyTopics({
    yearRangeStart,
    yearRangeEnd,
    subjectCode: selectedSubject,
    limit: 12,
  })

  if (isLoading) {
    return <LoadingSection title="Must-Study Predictions" height="h-96" />
  }

  if (isError) {
    return (
      <ErrorSection
        title="Failed to load predictions"
        onRetry={() => refetch()}
        height="h-96"
      />
    )
  }

  if (!data) return null

  const topics = data.topics || []
  const predictionYear = data.predictionYear || new Date().getFullYear()

  const contentGrid = topics.length === 0 ? (
    <p className="text-sm text-gray-500 text-center py-8">
      No prediction data available for the selected period
    </p>
  ) : (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {topics.map((topic) => (
        <div
          key={topic.topicCode}
          onClick={() => handleTopicClick(topic.topicCode)}
          className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm cursor-pointer transition-all"
        >
          {/* Rank Badge */}
          <div className="flex items-start justify-between gap-2 mb-2">
            <span
              className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white rounded-full"
              style={{ backgroundColor: getSubjectColor(topic.subjectCode, topic.subjectName) }}
            >
              {topic.rank}
            </span>
            <div className="flex items-center gap-1">
              <TrendIcon trend={topic.trendDirection} />
              <span
                className="text-xs font-medium px-1.5 py-0.5 rounded"
                style={{
                  backgroundColor: (topic.confidenceLevel ? CONFIDENCE_COLORS[topic.confidenceLevel] : '#6B7280') + '20',
                  color: topic.confidenceLevel ? CONFIDENCE_COLORS[topic.confidenceLevel] : '#6B7280',
                }}
              >
                {topic.confidenceLevel}
              </span>
            </div>
          </div>

          {/* Topic Name */}
          <h4 className="font-medium text-gray-900 text-sm line-clamp-2 mb-2">
            {topic.topicName || 'Unknown Topic'}
          </h4>

          {/* Subject Badge */}
          <div className="mb-3">
            <span
              className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full"
              style={{
                backgroundColor: getSubjectColor(topic.subjectCode, topic.subjectName) + '20',
                color: getSubjectColor(topic.subjectCode, topic.subjectName),
              }}
            >
              {topic.subjectName || topic.subjectCode || 'Unknown'}
            </span>
          </div>

          {/* Probability Bar */}
          <div className="mb-3">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-gray-600">Probability</span>
              <span
                className="font-medium"
                style={{ color: getSubjectColor(topic.subjectCode, topic.subjectName) }}
              >
                {((topic.probabilityScore ?? 0) * 100).toFixed(0)}%
              </span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${(topic.probabilityScore ?? 0) * 100}%`,
                  backgroundColor: getSubjectColor(topic.subjectCode, topic.subjectName),
                }}
              />
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="bg-gray-50 rounded p-2">
              <p className="text-gray-500">Expected Qs</p>
              <p className="font-medium text-gray-900">{(topic.expectedQuestions ?? 0).toFixed(1)}</p>
            </div>
            <div className="bg-gray-50 rounded p-2">
              <p className="text-gray-500">Avg/Exam</p>
              <p className="font-medium text-gray-900">{(topic.avgQuestionsPerExam ?? 0).toFixed(1)}</p>
            </div>
            <div className="bg-gray-50 rounded p-2">
              <p className="text-gray-500">Appearance</p>
              <p className="font-medium text-gray-900">{(topic.appearanceFrequency ?? 0).toFixed(0)}%</p>
            </div>
            <div className="bg-gray-50 rounded p-2">
              <p className="text-gray-500">Last Appeared</p>
              <p className="font-medium text-gray-900">{topic.lastAppearedYear ?? 'N/A'}</p>
            </div>
          </div>

          {/* Importance Score */}
          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
            <span className="text-xs text-gray-500">Importance</span>
            <span className="text-sm font-semibold text-gray-900">
              {(topic.importanceScore ?? 0).toFixed(1)}
            </span>
          </div>
        </div>
      ))}
    </div>
  )

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Header with Subject Tabs - always visible */}
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-600" />
              Must-Study Topics
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Predictions for NEET {predictionYear} based on historical analysis
            </p>
          </div>
          <div className="flex items-center gap-1 text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">
            {predictionYear}
          </div>
        </div>
        <SubjectTabs
          selectedSubject={selectedSubject}
          onSubjectChange={setSelectedSubject}
        />
      </div>

      {/* Content - locked for free users */}
      {isLocked ? (
        <AnalyticsLockedOverlay>
          {contentGrid}
        </AnalyticsLockedOverlay>
      ) : (
        contentGrid
      )}
    </div>
  )
}
